// pages/ChangePassword.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  LinearProgress,
  Divider,
  Grid,
  alpha,
  useTheme,
  Container,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  VpnKey as VpnKeyIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { ChangePasswordRequest } from '../types/index';

const ChangePassword: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<ChangePasswordRequest>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // UI state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  // Handle form input changes
  const handleInputChange = (field: keyof ChangePasswordRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });

    // Clear field-specific error
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }

    // Check password strength for new_password
    if (field === 'new_password') {
      setPasswordStrength({
        minLength: value.length >= 8,
        hasUppercase: /[A-Z]/.test(value),
        hasLowercase: /[a-z]/.test(value),
        hasNumber: /\d/.test(value),
        hasSpecial: /[@$!%*?&]/.test(value),
      });
    }

    // Clear confirm password error when user types
    if (field === 'confirm_password' && formErrors.confirm_password) {
      setFormErrors({ ...formErrors, confirm_password: '' });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Current password validation
    if (!formData.current_password) {
      errors.current_password = 'Current password is required';
    }

    // New password validation
    if (!formData.new_password) {
      errors.new_password = 'New password is required';
    } else if (formData.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.new_password)) {
      errors.new_password = 'Password must meet all requirements';
    }

    // Confirm password validation
    if (!formData.confirm_password) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (formData.new_password !== formData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }

    // Check if new password is same as current
    if (formData.current_password && formData.new_password && 
        formData.current_password === formData.new_password) {
      errors.new_password = 'New password must be different from current password';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authService.changePassword(formData);

      if (response.success) {
        setSuccessMessage(response.message || 'Password changed successfully');
        
        // Reset form
        setFormData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
        setPasswordStrength({
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecial: false,
        });

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (err: any) {
      console.error('Change password error:', err);
      
      // Handle backend validation errors
      if (err.data?.errors) {
        const backendErrors: Record<string, string> = {};
        Object.keys(err.data.errors).forEach(key => {
          const errorArray = err.data.errors[key];
          backendErrors[key] = Array.isArray(errorArray) ? errorArray[0] : errorArray;
        });
        setFormErrors(backendErrors);
      }
      
      setError(err.message || err.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/dashboard');
  };

  // Password strength color
  const getStrengthColor = () => {
    const metRequirements = Object.values(passwordStrength).filter(Boolean).length;
    if (metRequirements <= 2) return theme.palette.error.main;
    if (metRequirements <= 4) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Fade in={true} timeout={600}>
        <Box>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 2,
              }}
            >
              <VpnKeyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Change Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Keep your account secure by updating your password regularly
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Main Form */}
            <Grid size = {{xs:12, md:7}}>
              <Zoom in={true} timeout={800}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: `0 4px 20px ${alpha('#000', 0.08)}`,
                  }}
                >
                  {loading && <LinearProgress />}

                  <CardContent sx={{ p: 4 }}>
                    <Box
                      component="form"
                      onSubmit={handleSubmit}
                      noValidate
                    >
                      <Grid container spacing={3}>
                        {/* Current Password */}
                        <Grid size = {{xs:12}}>
                          <TextField
                            fullWidth
                            label="Current Password"
                            type={showCurrentPassword ? 'text' : 'password'}
                            required
                            value={formData.current_password}
                            onChange={handleInputChange('current_password')}
                            error={!!formErrors.current_password}
                            helperText={formErrors.current_password}
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    edge="end"
                                    disabled={loading}
                                  >
                                    {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid size = {{xs:12}}>
                          <Divider />
                        </Grid>

                        {/* New Password */}
                        <Grid size = {{xs:12}}>
                          <TextField
                            fullWidth
                            label="New Password"
                            type={showNewPassword ? 'text' : 'password'}
                            required
                            value={formData.new_password}
                            onChange={handleInputChange('new_password')}
                            error={!!formErrors.new_password}
                            helperText={formErrors.new_password}
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockOpenIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    edge="end"
                                    disabled={loading}
                                  >
                                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        {/* Confirm Password */}
                        <Grid size = {{xs:12}}>
                          <TextField
                            fullWidth
                            label="Confirm New Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            required
                            value={formData.confirm_password}
                            onChange={handleInputChange('confirm_password')}
                            onBlur={() => {
                              if (formData.new_password && formData.confirm_password &&
                                  formData.new_password !== formData.confirm_password) {
                                setFormErrors({ ...formErrors, confirm_password: 'Passwords do not match' });
                              }
                            }}
                            error={!!formErrors.confirm_password}
                            helperText={
                              formErrors.confirm_password ||
                              (formData.new_password && formData.confirm_password &&
                               formData.new_password === formData.confirm_password
                                ? '✓ Passwords match'
                                : '')
                            }
                            color={
                              formData.new_password && formData.confirm_password &&
                              formData.new_password === formData.confirm_password
                                ? 'success'
                                : 'primary'
                            }
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockOpenIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    disabled={loading}
                                  >
                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        {/* Action Buttons */}
                        <Grid size = {{xs:12}}>
                          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={handleCancel}
                              disabled={loading}
                              sx={{ borderRadius: 2, py: 1.5 }}
                            >
                              Cancel
                            </Button>
                            <Button
                              fullWidth
                              type="submit"
                              variant="contained"
                              disabled={loading}
                              sx={{
                                borderRadius: 2,
                                py: 1.5,
                                fontWeight: 600,
                                backgroundColor: '#996600',
                                '&:hover': {
                                  backgroundColor: '#664400',
                                },
                              }}
                            >
                              {loading ? 'Changing Password...' : 'Change Password'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>

            {/* Password Requirements */}
            <Grid size = {{xs:12, md:5}}>
              <Zoom in={true} timeout={1000}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Password Requirements
                    </Typography>
                  </Box>

                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {passwordStrength.minLength ? (
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="At least 8 characters"
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: passwordStrength.minLength
                              ? 'success.main'
                              : 'text.secondary',
                            fontWeight: passwordStrength.minLength ? 600 : 400,
                          },
                        }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {passwordStrength.hasUppercase ? (
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="One uppercase letter (A-Z)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: passwordStrength.hasUppercase
                              ? 'success.main'
                              : 'text.secondary',
                            fontWeight: passwordStrength.hasUppercase ? 600 : 400,
                          },
                        }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {passwordStrength.hasLowercase ? (
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="One lowercase letter (a-z)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: passwordStrength.hasLowercase
                              ? 'success.main'
                              : 'text.secondary',
                            fontWeight: passwordStrength.hasLowercase ? 600 : 400,
                          },
                        }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {passwordStrength.hasNumber ? (
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="One number (0-9)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: passwordStrength.hasNumber
                              ? 'success.main'
                              : 'text.secondary',
                            fontWeight: passwordStrength.hasNumber ? 600 : 400,
                          },
                        }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {passwordStrength.hasSpecial ? (
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary="One special character (@$!%*?&)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: {
                            color: passwordStrength.hasSpecial
                              ? 'success.main'
                              : 'text.secondary',
                            fontWeight: passwordStrength.hasSpecial ? 600 : 400,
                          },
                        }}
                      />
                    </ListItem>
                  </List>

                  {/* Password Strength Indicator */}
                  {formData.new_password && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Password Strength
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(Object.values(passwordStrength).filter(Boolean).length / 5) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha('#000', 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getStrengthColor(),
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="caption">
                      <strong>Security Tip:</strong> Use a unique password that you don't use for other accounts.
                      Consider using a password manager.
                    </Typography>
                  </Alert>
                </Paper>
              </Zoom>
            </Grid>
          </Grid>
        </Box>
      </Fade>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ChangePassword;